import { Constants } from "./constants";

/**
 * Canonical display label and color for every trial status.
 * Keyed by the raw Constants.trial_status value string.
 *
 * Usage:
 *   import { getTrialStatusLabel, getTrialStatusColor } from "@eagerdog/constants";
 *   getTrialStatusLabel("running") // → "Running"
 */
export interface ITrialStatusInfo {
  /** Human-readable display label */
  label: string;
  /** Color key used by web (StatusRenderer pill palette) and mobile (badge) */
  color: "grey" | "green" | "orange" | "blue";
}

export const TrialStatusInfo = new Map<string, ITrialStatusInfo>([
  [Constants.trial_status.not_started, { label: "Not Started", color: "grey" }],
  [Constants.trial_status.running, { label: "Running", color: "green" }],
  [Constants.trial_status.break, { label: "Break", color: "orange" }],
  [Constants.trial_status.complete, { label: "Complete", color: "blue" }],
]);

/**
 * Returns the display label for a trial status.
 * Missing/undefined values are treated as "Not Started" so every consumer
 * (web + mobile) formats un-set shows identically without needing a DB backfill.
 */
export function getTrialStatusLabel(status?: string): string {
  if (!status) {
    return TrialStatusInfo.get(Constants.trial_status.not_started)!.label;
  }
  return TrialStatusInfo.get(status)?.label ?? status;
}

/**
 * Returns the color key for a trial status, for consistent status-pill coloring.
 * Missing/undefined values are treated as "Not Started" (grey).
 */
export function getTrialStatusColor(status?: string): "grey" | "green" | "orange" | "blue" {
  if (!status) {
    return "grey";
  }
  return TrialStatusInfo.get(status)?.color ?? "grey";
}
